import type { PostButton, ButtonPreset } from '../lib/types';
import { Button } from './ui/Button';
import './PostButtons.css';

interface Props {
  /** Team-defined link buttons. Renders nothing when empty. */
  buttons: PostButton[];
}

/** Each team-facing preset maps onto a Button primitive variant. The primitive
 *  owns the shape (pill), symmetric padding, touch target, and hover - this
 *  renderer only picks the look + wires the link. (`link` and `ghost` share the
 *  quiet ghost variant; `outline`->secondary, `download`->soft bone fill.) */
const PRESET_VARIANT: Record<ButtonPreset, 'primary' | 'accent' | 'secondary' | 'soft' | 'ghost'> = {
  primary: 'primary',
  accent: 'accent',
  outline: 'secondary',
  download: 'soft',
  ghost: 'ghost',
  link: 'ghost',
};

/** True when `url` resolves to the same origin as the current page, so a
 *  `download` attribute will actually be honored by the browser. */
function isSameOrigin(url: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return new URL(url, window.location.href).origin === window.location.origin;
  } catch {
    return false;
  }
}

export function PostButtons({ buttons }: Props) {
  if (!buttons || buttons.length === 0) return null;

  return (
    <div className="post-buttons">
      {buttons.map((b, i) => {
        const isDownload = b.preset === 'download';
        const download = isDownload && isSameOrigin(b.url);
        return (
          <Button
            key={`${b.url}-${i}`}
            as="a"
            variant={PRESET_VARIANT[b.preset]}
            size="sm"
            href={b.url}
            target="_blank"
            rel="noopener noreferrer"
            {...(download ? { download: '' } : {})}
          >
            {isDownload && <DownloadGlyph />}
            <span>{b.label}</span>
            {b.preset === 'link' && <ExternalGlyph />}
          </Button>
        );
      })}
    </div>
  );
}

function DownloadGlyph() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 3v12m0 0l-4-4m4 4l4-4M5 21h14"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ExternalGlyph() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M14 4h6v6m0-6L10 14M19 13v5a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
